import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Shield, RefreshCw, GripVertical } from 'lucide-react';
import { adminManagementApi } from '../../services/api';

const SecurityOverviewPage = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminManagementApi.getSummary();
      setSummary(res.data);
    } catch (err) {
      console.error('Failed to load summary:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="stui-module-loading">Loading...</div>;

  return (
    <div className="stui-module-container">
      <div className="stui-module-header">
        <div className="stui-module-header-left">
          <Shield size={20} />
          <h1>Security Overview</h1>
          <span className="stui-module-header-meta">
            {summary ? `${summary.activeUsers} active users • ${summary.databases} databases • ${summary.permissions} permissions` : ''}
          </span>
        </div>
        <div className="stui-module-header-actions">
          <div className="stui-action-menu" ref={menuRef}>
            <button className="stui-action-menu-btn" onClick={() => setMenuOpen(!menuOpen)} title="Actions"><GripVertical size={16} /></button>
            {menuOpen && (
              <div className="stui-action-menu-dropdown">
                <button className="stui-action-menu-item" onClick={() => { setMenuOpen(false); load(); }}><RefreshCw size={16} /><span>Refresh</span></button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="stui-detail-content">
        <div>
          <h1>RBAC Security Model</h1>
          <p>PMD uses a <strong>Role-Based Access Control</strong> model with per-user overrides. Roles define behavioral floors, permissions control granular feature access, and Grant/Deny overrides allow per-user exceptions.</p>


          <h2 className="stui-detail-section-header">Role Hierarchy</h2>
          <table className="stui-data-grid">
            <thead>
              <tr>
                <th>Role</th>
                <th>Scope</th>
                <th>Key Capabilities</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>SysAdmin</strong></td>
                <td>Global</td>
                <td>God mode. Page/UI config. DB sync. Manage all roles.</td>
              </tr>
              <tr>
                <td><strong>AppAdmin</strong></td>
                <td>Per-DB</td>
                <td>Full DB access. Manage users up to AppAdmin. All permissions implied.</td>
              </tr>
              <tr>
                <td><strong>PowerUser</strong></td>
                <td>Per-DB</td>
                <td>Edit all modules. Datasheet edit. Delete. Config tables.</td>
              </tr>
              <tr>
                <td><strong>User</strong></td>
                <td>Per-DB</td>
                <td>Edit only permitted modules. No delete. No config.</td>
              </tr>
              <tr>
                <td><strong>Guest</strong></td>
                <td>Per-DB</td>
                <td>Read-only. View + export.</td>
              </tr>
            </tbody>
          </table>


          <h2 className="stui-detail-section-header">Permission Resolution Logic</h2>
          <ol>
            <li>Is user <strong>SysAdmin</strong> (global role)? → <strong>Allow everything</strong></li>
            <li>Is user <strong>AppAdmin</strong> for this database? → <strong>Allow everything in this DB</strong></li>
            <li>Check <strong>UserPermissions</strong> for explicit Grant/Deny override</li>
            <li>Fall back to <strong>DefRoles</strong> (capability matrix) for this role + permission</li>
            <li>No entry found? → <strong>Deny</strong></li>
          </ol>


          <h2 className="stui-detail-section-header">Schema — 7 Tables</h2>
          <table className="stui-data-grid">
            <thead>
              <tr>
                <th>Table</th>
                <th>Purpose</th>
                <th>Records</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>AppSec_Organizations</code></td>
                <td>Company/entity grouping</td>
                <td>{summary?.organizations || 0}</td>
              </tr>
              <tr>
                <td><code>AppSec_Users</code></td>
                <td>User profiles (+ OrganizationId FK)</td>
                <td>{summary?.users || 0}</td>
              </tr>
              <tr>
                <td><code>AppSec_Databases</code></td>
                <td>Project database instances (GWA, PNA, GFS)</td>
                <td>{summary?.databases || 0}</td>
              </tr>
              <tr>
                <td><code>AppSec_UserRoles</code></td>
                <td>User + Database = Role assignment</td>
                <td>{summary?.userRoles || 0}</td>
              </tr>
              <tr>
                <td><code>AppSec_DefPermissions</code></td>
                <td>Master permission definitions</td>
                <td>{summary?.permissions || 0}</td>
              </tr>
              <tr>
                <td><code>AppSec_DefRoles</code></td>
                <td>Default capability matrix (role → permission → access)</td>
                <td>{summary?.defRoleEntries || 0}</td>
              </tr>
              <tr>
                <td><code>AppSec_UserPermissions</code></td>
                <td>Per-user Grant/Deny overrides</td>
                <td>{summary?.userOverrides || 0}</td>
              </tr>
            </tbody>
          </table>


          <h2 className="stui-detail-section-header">Admin Pages</h2>
          <table className="stui-data-grid">
            <thead>
              <tr>
                <th>Page</th>
                <th>Route</th>
                <th>Purpose</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Security Overview</strong></td>
                <td><code>/admin/security</code></td>
                <td>This page — RBAC docs and reference</td>
              </tr>
              <tr>
                <td><strong>Users</strong></td>
                <td><code>/admin/users</code></td>
                <td>User management, role assignment, permission overrides</td>
              </tr>
              <tr>
                <td><strong>Roles & Capabilities</strong></td>
                <td><code>/admin/roles</code></td>
                <td>DefRoles capability matrix viewer/editor</td>
              </tr>
              <tr>
                <td><strong>Permissions</strong></td>
                <td><code>/admin/permissions</code></td>
                <td>DefPermissions master list</td>
              </tr>
              <tr>
                <td><strong>Databases</strong></td>
                <td><code>/admin/databases</code></td>
                <td>Database instance registry</td>
              </tr>
              <tr>
                <td><strong>Organizations</strong></td>
                <td><code>/admin/organizations</code></td>
                <td>Organization management</td>
              </tr>
            </tbody>
          </table>


          <h2 className="stui-detail-section-header">Implementation Status</h2>
          <table className="stui-data-grid">
            <thead>
              <tr>
                <th>Step</th>
                <th>Status</th>
                <th>Scope</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Step 37</strong></td>
                <td>Complete</td>
                <td>Schema, backend CRUD, admin UI pages, seed data. Build everything, hook up nothing.</td>
              </tr>
              <tr>
                <td><strong>Step 51</strong></td>
                <td>Not Started</td>
                <td>Team review of RBAC model. Refine roles, permissions, validate with real scenarios.</td>
              </tr>
              <tr>
                <td><strong>Step 52</strong></td>
                <td>Not Started</td>
                <td>Enforcement wiring. Middleware integration. Frontend permission checks.</td>
              </tr>
              <tr>
                <td><strong>Step 53</strong></td>
                <td>Not Started</td>
                <td>Legacy Admin_Users cleanup. Migration from per-project tables. Deprecation.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SecurityOverviewPage;
